import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { OptionType } from '../elements/FormAutocomplete'
import { BaseOptionType } from '../helpers/getFormSchema'
import {
  getItemSx,
  getMenuProps,
  getValueSx,
  typographySx,
} from '../styles/form-select.styles'

type CustomSelectProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  handleEditValue?: (value: FieldValues) => T
  multiple?: boolean
  fullWidth: boolean
  items: BaseOptionType[] | string[]
  fullDotationRegions?: string[]
  disabled?: boolean
  onChange?: (value: BaseOptionType) => void
  disableDefaultOnChange?: boolean
  error?: boolean
  correctItems?: BaseOptionType[]
  withLabel?: boolean
  width?: string | number
}

const FormSelect = <T extends FieldValues>(props: CustomSelectProps<T>) => {
  const {
    control,
    fullWidth,
    items,
    label,
    name,
    placeholder,
    onChange: handleOnChange,
    handleEditValue,
    disableDefaultOnChange,
    error,
    correctItems,
    withLabel,
    width,
    ...otherProps
  } = props

  const renderValue = (
    _value: string | OptionType,
    value: string | OptionType,
  ) => {
    return Array.isArray(value) ? (
      <Typography sx={typographySx}>
        {getCategoriesValue(value, items as unknown as BaseOptionType[])}
      </Typography>
    ) : (
      <MenuItem component='li' sx={getValueSx(otherProps.disabled)}>
        {typeof value === 'string' && value}
        {!!value && typeof value === 'object' && (
          <>
            {(value as BaseOptionType)?.iconSrc && (
              <Box
                mr={'4px'}
                component='img'
                src={(value as BaseOptionType)?.iconSrc}
              />
            )}
            <Typography sx={valueTypographySx}>
              {(value as BaseOptionType)?.value}
            </Typography>
          </>
        )}
      </MenuItem>
    )
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, ...field } }) => {
        const renderItems = items.filter((item) =>
          typeof item === 'object' ? item?.key !== value?.key : item !== value,
        )
        const isDisabled = !renderItems.length || otherProps.disabled

        return (
          <FormControl fullWidth={fullWidth} error={error}>
            <InputLabel
              shrink={
                !withLabel ? false : (value?.key ?? value) ? true : undefined
              }
              sx={labelSx(withLabel, isDisabled)}
            >
              {!value && !withLabel && placeholder}
              {withLabel && label}
            </InputLabel>
            <Select
              label={undefined}
              value={value}
              sx={selectSx}
              IconComponent={() => (
                <img
                  alt={''}
                  src={getImageSrc('downIcon')}
                  style={iconStyle(isDisabled)}
                />
              )}
              error={error}
              renderValue={(_value) => renderValue(_value, value)}
              MenuProps={getMenuProps(withLabel, width)}
              native={false}
              displayEmpty={true}
              {...field}
              {...otherProps}
            >
              {renderItems.map((item: BaseOptionType | string) => {
                const transparentBg =
                  (value?.key !== (item as BaseOptionType)?.key ||
                    value !== item) &&
                  !Array.isArray(value)
                const activeValue =
                  Array.isArray(value) && value[(item as BaseOptionType).key]

                return (
                  <MenuItem
                    key={typeof item !== 'string' ? item?.key : item}
                    value={value}
                    component='li'
                    onClick={() => {
                      !disableDefaultOnChange && onChange(item)
                      handleOnChange && handleOnChange(item as BaseOptionType)
                    }}
                    sx={getItemSx(transparentBg, activeValue)}
                  >
                    {typeof item === 'string' && item}

                    {typeof item !== 'string' && (
                      <>
                        {item.iconSrc && (
                          <Box
                            sx={{ marginRight: '10px' }}
                            component='img'
                            src={item.iconSrc}
                          />
                        )}
                        <Typography sx={{ textWrap: 'initial' }}>
                          {item.value}
                        </Typography>
                      </>
                    )}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )
      }}
    />
  )
}

const labelSx = (withLabel?: boolean, isDisabled?: boolean) => ({
  color: withLabel ? 'rgba(0, 0, 0, 0.6)' : 'black!important',
  paddingBottom: withLabel ? '4px' : undefined,
  backgroundColor: withLabel ? '#fff' : undefined,
  opacity: isDisabled ? '.3' : '1',
})

const valueTypographySx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}

const selectSx = {
  '.MuiSelect-select': {
    paddingRight: '20px',
  },
}

const iconStyle = (isDisabled?: boolean) => ({
  position: 'absolute' as 'absolute',
  height: '23px',
  width: '23px',
  right: '14px',
  opacity: isDisabled ? '.3' : '.8',
  pointerEvents: 'none' as 'none',
})

const getCategoriesValue = (value: boolean[], items: BaseOptionType[]) =>
  items
    .filter((_, idx) => value[idx])
    .map((item) => item.value)
    .join(', ')

export default observer(FormSelect)
